/**
 * Übersetzung zwischen URL-Suchparametern und Listenzustand.
 *
 * Bewusst als reine Funktionen ausgelagert: Der Vertrag „ein geteilter Link stellt denselben
 * Arbeitskontext her" ist Logik und wird getestet, nicht die Komponente drumherum.
 */
export type StatusFilter = 'all' | 'Pending' | 'Confirmed' | 'Cancelled';
export type SortField = 'property' | 'guest' | 'checkInDate' | 'status' | 'channel' | 'totalPrice';

export interface SortState {
  field: SortField;
  direction: 'asc' | 'desc';
}

export interface BookingsFilterState {
  status: StatusFilter;
  dateFrom: string;
  dateTo: string;
  propertyId: string;
  /** „Vergangene zeigen“: hebt den Filter „Abreise ab heute“ auf. */
  showPast: boolean;
}

export interface BookingsSearch {
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  propertyId?: string;
  q?: string;
  sort?: string;
  page?: number;
  size?: number;
  past?: boolean;
}

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZES = [25, 50, 100, 200] as const;

export const EMPTY_FILTERS: BookingsFilterState = {
  status: 'all',
  dateFrom: '',
  dateTo: '',
  propertyId: '',
  showPast: false,
};

const STATUSES: StatusFilter[] = ['all', 'Pending', 'Confirmed', 'Cancelled'];
const SORT_FIELDS: SortField[] = [
  'property',
  'guest',
  'checkInDate',
  'status',
  'channel',
  'totalPrice',
];

/** Ab heute zuerst, wer als Nächstes kommt; mit Vergangenen zuerst die jüngsten. */
export function defaultSort(showPast: boolean): SortState {
  return { field: 'checkInDate', direction: showPast ? 'desc' : 'asc' };
}

export function isFiltered(filters: BookingsFilterState, query: string): boolean {
  return (
    filters.status !== 'all' ||
    filters.dateFrom !== '' ||
    filters.dateTo !== '' ||
    filters.propertyId !== '' ||
    query.trim() !== ''
  );
}

export function filtersFromSearch(search: BookingsSearch): BookingsFilterState {
  return {
    status: STATUSES.find((s) => s === search.status) ?? 'all',
    dateFrom: search.dateFrom ?? '',
    dateTo: search.dateTo ?? '',
    propertyId: search.propertyId ?? '',
    showPast: search.past === true,
  };
}

export function sortFromSearch(search: BookingsSearch): SortState {
  const [field, direction] = (search.sort ?? '').split(':');
  const known = SORT_FIELDS.find((f) => f === field);
  if (!known || (direction !== 'asc' && direction !== 'desc'))
    return defaultSort(search.past === true);
  return { field: known, direction };
}

/**
 * Apartment und Gast kann der Server nicht sortieren (kein JOIN); die sortiert die Seite selbst,
 * der Server liefert dann in der Standardreihenfolge.
 */
export function toServerSort(sort: SortState, showPast: boolean): string {
  const serverSide = sort.field !== 'property' && sort.field !== 'guest';
  const effective = serverSide ? sort : defaultSort(showPast);
  return `${effective.field} ${effective.direction}`;
}

/**
 * Defaults werden weggelassen statt mitgeschrieben — eine ungefilterte Liste soll eine saubere
 * URL haben, sonst sieht jeder geteilte Link nach Sonderfall aus.
 */
export function searchFromState(state: {
  filters: BookingsFilterState;
  sort: SortState;
  page: number;
  pageSize: number;
  query: string;
}): BookingsSearch {
  const fallback = defaultSort(state.filters.showPast);
  const sortIsDefault =
    state.sort.field === fallback.field && state.sort.direction === fallback.direction;
  return {
    status: state.filters.status === 'all' ? undefined : state.filters.status,
    dateFrom: state.filters.dateFrom || undefined,
    dateTo: state.filters.dateTo || undefined,
    propertyId: state.filters.propertyId || undefined,
    q: state.query.trim() || undefined,
    sort: sortIsDefault ? undefined : `${state.sort.field}:${state.sort.direction}`,
    page: state.page > 1 ? state.page : undefined,
    size: state.pageSize === DEFAULT_PAGE_SIZE ? undefined : state.pageSize,
    past: state.filters.showPast || undefined,
  };
}
